import * as React from "react";
import { cn } from "@/shared/lib/utils";

export interface SwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "lg";
  label?: string;
  helper?: string;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      onChange,
      disabled,
      className,
      size = "lg",
      label,
      helper,
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled) {
        onChange?.(!checked);
      }
    };

    const sizes = {
      sm: {
        track: "w-9 h-5",
        thumb: "w-4 h-4",
        translate: "translate-x-4",
      },
      lg: {
        track: "sm:w-10 sm:h-6 w-9 h-5",
        thumb: "sm:w-5 sm:h-5 w-4 h-4",
        translate: "sm:translate-x-4 translate-x-4",
      },
    };

    const { track, thumb, translate } = sizes[size];

    return (
      <div className="flex items-center gap-3 rtl:flex-row-reverse">
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            "relative inline-flex shrink-0 cursor-pointer rounded-full",
            "border-2 transition-colors duration-200 ease-in-out",
            "focus-visible:outline-none",
            "disabled:cursor-not-allowed",
            track,
            "bg-grey-100 border-grey-100",
            "data-[checked=true]:bg-brand-green data-[checked=true]:border-brand-green",
            "hover:bg-grey-200 hover:border-grey-200",
            "data-[checked=true]:hover:bg-green-800 data-[checked=true]:hover:border-green-800",
            "active:bg-grey-300 active:border-grey-300",
            "data-[checked=true]:active:bg-green-900 data-[checked=true]:active:border-green-900",
            "focus-visible:bg-grey-100 focus-visible:border-grey-200 focus-visible:border-2",
            "data-[checked=true]:focus:border-green-900 data-[checked=true]:focus:border-2 focus:border-2 focus:border-grey-200",
            "disabled:bg-[#F7F7F7] disabled:border-grey-50",
            "disabled:data-[checked=true]:bg-green-300 disabled:data-[checked=true]:border-green-300",
            className
          )}
          data-checked={checked}
        >
          <span
            className={cn(
              "pointer-events-none inline-block rounded-full shadow-lg",
              "ring-0 transition duration-200 ease-in-out",
              "bg-white",
              disabled && !checked && "bg-grey-100",
              disabled && checked && "bg-green-100",
              thumb,
              checked ? translate : "translate-x-0",
              checked
                ? size === "sm"
                  ? "rtl:-translate-x-4"
                  : "rtl:-translate-x-4 rtl:sm:-translate-x-4"
                : "rtl:translate-x-0"
            )}
          />
        </button>
        {(label || helper) && (
          <div className="flex flex-col">
            {label && (
              <label
                className={cn(
                  "text-xs font-medium",
                  disabled ? "text-grey-200" : "text-grey-900"
                )}
              >
                {label}
              </label>
            )}
            {helper && (
              <p
                className={cn(
                  "text-xxs font-medium",
                  disabled ? "text-grey-200" : "text-grey-500"
                )}
              >
                {helper}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);
Switch.displayName = "Switch";

export { Switch };
